import { useState, useEffect, useRef, useCallback } from 'react';
import axios from 'axios';
import hgTree from '@/assets/hg_tree.png';

// Public, un-gated signing flow. Like PublicReviewForm, this page renders OUTSIDE
// the Clerk-gated app, so no auth interceptor is in play — it POSTs to the public
// endpoint /api/public/agreements/submit.
const API = '/api';

const STEPS = [
  { title: 'Account information', sub: 'Tell us who you are and where things should go. This sets up your Henry-Griffitts account.' },
  { title: 'Fees & authorizations', sub: 'Choose how you’d like to pay, then confirm the authorizations below.' },
  { title: 'License terms', sub: 'The plain-language terms of your Fitter License & Account Agreement.' },
  { title: 'Initial the key clauses', sub: 'A quick confirmation that you’ve read and agree to the key clauses.' },
  { title: 'Review & sign', sub: 'Check the details, then sign to complete your agreement.' },
];

const LABELS = ['Account', 'Fees', 'Terms', 'Initials', 'Sign'];

// A FACILITY PARTNER agreement (agreement_type "facility_partner") runs through this same page:
// same steps, same terms renderer, same initials, same signature pad. Only the words and the
// account fields differ, and the terms + initials rows come from the backend as they do for a
// fitter. Nothing here decides the type — the invite response does.
const FACILITY = 'facility_partner';
const FACILITY_STEPS = [
  { title: 'Facility information', sub: 'The facility schedule for your Facility Partner Agreement. Check what we prefilled and complete the rest.' },
  { title: 'Fees & card on file', sub: 'The start-up fee, and how you’d like to pay the license fee.' },
  { title: 'Agreement terms', sub: 'The full terms of your Facility Partner Agreement.' },
  { title: 'Initial the key clauses', sub: 'A quick confirmation that you’ve read and agree to the key clauses.' },
  { title: 'Review & sign', sub: 'Check the details, then sign to complete your agreement.' },
];

const INIT_ROWS = [
  { key: 'fee_choice', label: 'Fee choice', desc: 'your selected monthly or annual license fee.' },
  { key: 'authorizations', label: 'Authorizations', desc: 'card on file and background/credit check.' },
  { key: 'return_of_system', label: 'Return of the system', desc: '30-day window, you pay shipping, $3,500 if not returned.' },
];

// Initials are TYPED BY THE SIGNER — never generated. Initialling a clause individually is
// evidence that this person read that clause; a value the software derived from a form field
// is evidence of nothing, and one of these clauses carries a $3,500 liability. The old
// helper built initials from the business name (so "Test Account" initialled as "TA") and
// fell back to a HARDCODED '✓ RH' — Randall's own initials, which would have landed on a
// stranger's liability clause if both name fields were blank. Both are gone deliberately.
// Do not reintroduce a default, a prefill, or a derived value here.
const INITIALS_MAX = 5;

function normalizeInitials(raw) {
  // Letters only, uppercased. Keeps "R.H." and "rh" landing on the same stored value.
  return String(raw || '').replace(/[^A-Za-z]/g, '').toUpperCase().slice(0, INITIALS_MAX);
}

// `**bold**` runs inside a clause body. Mirrors agreement_terms.split_bold on the Python
// side so the page and the PDF emphasise the same words. Nothing else is markup, and the
// text is never passed to dangerouslySetInnerHTML — it is split into React nodes.
function richText(text, keyPrefix) {
  return String(text || '').split('**').map((chunk, i) => (
    chunk === '' ? null
      : i % 2 === 1
        ? <b key={`${keyPrefix}-${i}`}>{chunk}</b>
        : <span key={`${keyPrefix}-${i}`}>{chunk}</span>
  ));
}

function makeLocalRef() {
  const s = Math.random().toString(36).slice(2, 7).toUpperCase();
  return `HG-${new Date().getFullYear()}-${s}`;
}

export default function FitterSigning() {
  const [step, setStep] = useState(0);
  const [submitting, setSubmitting] = useState(false);
  const [errorMsg, setErrorMsg] = useState('');
  const [reference, setReference] = useState('—');

  // Step 0 — account fields
  const [bizName, setBizName] = useState('');
  const [contactName, setContactName] = useState('');
  const [phone, setPhone] = useState('');
  const [email, setEmail] = useState('');
  const [courseOrClub, setCourseOrClub] = useState('');
  const [authorizedFitters, setAuthorizedFitters] = useState('');
  const [addressLine1, setAddressLine1] = useState('');
  const [city, setCity] = useState('');
  const [stateField, setStateField] = useState('');
  const [postalCode, setPostalCode] = useState('');
  // No default and no geo-guess. An unanswered country must be distinguishable from a confirmed
  // one: a prefilled 'United States' would be indistinguishable from a fitter who chose it, and
  // it decides which wholesale price list they receive.
  const [country, setCountry] = useState('');
  // Generated from the backend's COUNTRY_TO_REGION so the form cannot offer a country that has
  // no price-list region. Never hard-coded here.
  const [countryOptions, setCountryOptions] = useState([]);
  const [countryOther, setCountryOther] = useState('__other__');
  // Billing defaults to "same as shipping" — the common case, and it keeps the step short.
  // Defaulting to unticked would make "not answered" look like "genuinely different",
  // which is the trap `country` fell into when it defaulted to United States.
  const [billingSame, setBillingSame] = useState(true);
  const [billingLine1, setBillingLine1] = useState('');
  const [billingCity, setBillingCity] = useState('');
  const [billingState, setBillingState] = useState('');
  const [billingPostal, setBillingPostal] = useState('');
  const [billingCountry, setBillingCountry] = useState('');
  const [businessType, setBusinessType] = useState('');
  const [acceptsReferrals, setAcceptsReferrals] = useState(''); // '', 'yes', 'no'

  // Step 1 — fee + authorizations
  const [feeChoice, setFeeChoice] = useState('monthly');
  // Item 3: no cardOnFile/backgroundCheck state. Those were checkboxes implying a choice
  // that did not exist, and nothing enforced them. Signing IS the authorisation now, and
  // the server derives the recorded consent from the signature.
  const [onsiteOptin, setOnsiteOptin] = useState(false); // optional onsite training (default no)

  // Step 2 — terms scroll gate
  const [scrolledToEnd, setScrolledToEnd] = useState(false);
  const termsRef = useRef(null);

  // Step 3 — initials (state lives at line ~109 with the normalizer it depends on)

  // Step 4 — signature canvas
  const canvasRef = useRef(null);
  const drawing = useRef(false);
  const lastPt = useRef({ x: 0, y: 0 });
  const [hasSignature, setHasSignature] = useState(false);

  // ---- Invite token (Phase 2.5): the flow is gated by a valid invite link ----
  const [token] = useState(() => {
    try { return new URLSearchParams(window.location.search).get('token') || ''; } catch { return ''; }
  });
  const [inviteState, setInviteState] = useState('loading'); // loading | valid | invalid

  // The license terms, fetched with the invite. They are NOT written out in this file:
  // the signed PDF renders backend/agreement_terms.py, and a second hand-maintained copy
  // in JSX is exactly how a licensee's counterpart quietly stops matching what they read
  // before signing. One list, two renderers.
  const [terms, setTerms] = useState(null);

  // Facility partner agreement state. Empty for a fitter license, and unused by it.
  const [agreementType, setAgreementType] = useState('');
  const isFacility = agreementType === FACILITY;
  const [facilityTypes, setFacilityTypes] = useState([]);
  const [fees, setFees] = useState(null);
  const [dba, setDba] = useState('');
  const [facilityType, setFacilityType] = useState('');
  const [signerTitle, setSignerTitle] = useState('');
  const [fitterName, setFitterName] = useState('');
  const [fitterEmail, setFitterEmail] = useState('');
  const [fitterPhone, setFitterPhone] = useState('');
  const [cardSetupPath, setCardSetupPath] = useState('');

  useEffect(() => {
    let alive = true;
    if (!token) { setInviteState('invalid'); return undefined; }
    (async () => {
      try {
        const r = await axios.get(`${API}/public/agreements/invite/${encodeURIComponent(token)}`);
        if (!alive) return;
        if (r.data?.name) setContactName(r.data.name);
        if (r.data?.email) setEmail(r.data.email);
        if (Array.isArray(r.data?.country_options)) setCountryOptions(r.data.country_options);
        if (r.data?.country_other) setCountryOther(r.data.country_other);
        if (Array.isArray(r.data?.terms?.sections)) setTerms(r.data.terms);
        if (r.data?.agreement_type === FACILITY) {
          const p = r.data.prefill || {};
          setAgreementType(FACILITY);
          setFacilityTypes(r.data.facility_types || []);
          setFees(r.data.fees || null);
          setBizName(p.business_name || '');
          setDba(p.dba || '');
          setFacilityType(p.facility_type || '');
          setSignerTitle(p.signer_title || '');
          if (p.phone) setPhone(p.phone);
          setAddressLine1(p.address_line1 || '');
          setCity(p.city || '');
          setStateField(p.state || '');
          setPostalCode(p.postal_code || '');
          if (p.country) setCountry(p.country);
          setFitterName(p.designated_fitter_name || '');
          setFitterEmail(p.designated_fitter_email || '');
          setFitterPhone(p.designated_fitter_phone || '');
        }
        setInviteState('valid');
      } catch {
        if (alive) setInviteState('invalid');
      }
    })();
    return () => { alive = false; };
  }, [token]);

  // Per-clause typed initials: { fee_choice: 'RH', authorizations: 'RH', ... }. Starts EMPTY
  // for every clause — an empty box is an unanswered clause and blocks Continue.
  const [initialsByClause, setInitialsByClause] = useState({});

  // ---- Terms scroll gate ----
  const termsScrolled = useCallback((el) => {
    if (!el) return;
    if (el.scrollTop + el.clientHeight >= el.scrollHeight - 24) {
      setScrolledToEnd(true);
    }
  }, []);

  useEffect(() => {
    if (step !== 2) return;
    // Terms not loaded yet: an EMPTY box trivially satisfies "scrolled to the end", which
    // would wave the signer past a terms step showing no terms. Wait for the sections.
    if (!terms?.sections?.length) return;
    const el = termsRef.current;
    if (!el) return;
    // If the terms fit without scrolling, the gate is already satisfied.
    if (el.scrollHeight <= el.clientHeight + 24) setScrolledToEnd(true);
  }, [step, terms]);

  // ---- Signature canvas setup ----
  useEffect(() => {
    if (step !== 4) return;
    const canvas = canvasRef.current;
    if (!canvas) return;
    const ratio = window.devicePixelRatio || 1;
    const rect = canvas.getBoundingClientRect();
    canvas.width = rect.width * ratio;
    canvas.height = rect.height * ratio;
    const ctx = canvas.getContext('2d');
    ctx.scale(ratio, ratio);
    ctx.lineWidth = 2.2;
    ctx.lineCap = 'round';
    ctx.lineJoin = 'round';
    ctx.strokeStyle = '#1A3A6B';
  }, [step]);

  const ptFromEvent = (e) => {
    const canvas = canvasRef.current;
    const rect = canvas.getBoundingClientRect();
    const src = e.touches && e.touches.length ? e.touches[0] : e;
    return { x: src.clientX - rect.left, y: src.clientY - rect.top };
  };

  const startDraw = (e) => {
    e.preventDefault();
    drawing.current = true;
    lastPt.current = ptFromEvent(e);
  };

  const moveDraw = (e) => {
    if (!drawing.current) return;
    e.preventDefault();
    const ctx = canvasRef.current.getContext('2d');
    const p = ptFromEvent(e);
    ctx.beginPath();
    ctx.moveTo(lastPt.current.x, lastPt.current.y);
    ctx.lineTo(p.x, p.y);
    ctx.stroke();
    lastPt.current = p;
    if (!hasSignature) setHasSignature(true);
  };

  const endDraw = (e) => {
    if (e) e.preventDefault();
    drawing.current = false;
  };

  const clearSig = () => {
    const canvas = canvasRef.current;
    if (!canvas) return;
    const ctx = canvas.getContext('2d');
    ctx.clearRect(0, 0, canvas.width, canvas.height);
    setHasSignature(false);
  };

  const setClauseInitials = (key, raw) => {
    setInitialsByClause((prev) => ({ ...prev, [key]: normalizeInitials(raw) }));
  };

  // ---- Validation gating ----
  // A clause counts as initialled only when the SIGNER typed at least two letters into it.
  // Nothing here can be satisfied by a value the app produced.
  const clauseInitialled = (key) => (initialsByClause[key] || '').length >= 2;
  // A facility's rows come from its terms (two boxes in its docx); a fitter keeps these three.
  const initRows = (isFacility && Array.isArray(terms?.initial_rows)) ? terms.initial_rows : INIT_ROWS;
  const allInitialed = initRows.every((r) => clauseInitialled(r.key));

  const canContinue = () => {
    // Country is REQUIRED — it selects the wholesale price list, and there is no safe default
    // to fall back on. The other account fields keep their existing (unvalidated) behaviour;
    // this gate is deliberately narrow.
    // A revealed billing block must actually be filled in, or unticking the box and moving
    // on would record "billing is different" with nothing to show for it — worse than not
    // asking, because the record would then assert a distinction it cannot supply.
    if (step === 0) {
      if (!country) return false;
      if (isFacility && !bizName.trim()) return false;
      if (!billingSame && !(billingLine1.trim() && billingCity.trim() && billingPostal.trim())) return false;
      return true;
    }
    // Both conditions: the terms must have arrived AND been scrolled through. Without the
    // first, a failed terms fetch would present an empty box that reads as "nothing to read".
    if (step === 2) return !!terms?.sections?.length && scrolledToEnd;
    if (step === 3) return allInitialed;
    if (step === 4) return hasSignature;
    return true;
  };

  const isSignStep = step === 4;

  const jumpToStep = (i) => {
    // Clicking a progress step jumps only to steps <= current.
    if (i <= step) {
      setErrorMsg('');
      setStep(i);
    }
  };

  const go = async (dir) => {
    setErrorMsg('');
    if (dir < 0) {
      if (step > 0) setStep((s) => s - 1);
      return;
    }
    // forward
    if (!canContinue()) return;
    if (isSignStep) {
      await submit();
      return;
    }
    setStep((s) => s + 1);
  };

  const submit = async () => {
    if (submitting) return;
    if (!hasSignature) return;
    setSubmitting(true);
    setErrorMsg('');
    const acceptsReferralsBool =
      acceptsReferrals === 'yes' ? true : acceptsReferrals === 'no' ? false : null;
    const signatureImage = canvasRef.current ? canvasRef.current.toDataURL('image/png') : '';
    const body = {
      token,
      account: {
        business_name: bizName,
        contact_name: contactName,
        email,
        phone,
        course_or_club: courseOrClub,
        authorized_fitters: authorizedFitters,
        address_line1: addressLine1,
        city,
        state: stateField,
        postal_code: postalCode,
        country,
        // Sent as typed. When billing is the same we send the FLAG and leave the fields
        // empty rather than duplicating the shipping values — a copy taken at signing
        // would silently go stale the moment the shipping address were corrected.
        billing_same_as_shipping: billingSame,
        billing_address_line1: billingSame ? '' : billingLine1,
        billing_city: billingSame ? '' : billingCity,
        billing_state: billingSame ? '' : billingState,
        billing_postal_code: billingSame ? '' : billingPostal,
        billing_country: billingSame ? '' : billingCountry,
        business_type: businessType,
        accepts_referrals: acceptsReferralsBool,
        // The facility schedule. Sent only for a facility agreement, so a fitter's payload is
        // exactly what it was.
        ...(isFacility ? {
          dba, facility_type: facilityType, signer_title: signerTitle,
          designated_fitter_name: fitterName, designated_fitter_email: fitterEmail,
          designated_fitter_phone: fitterPhone,
        } : {}),
      },
      fee_choice: feeChoice,
      // Not sent as a fitter choice — the server derives both from the signature and
      // ignores anything the client claims here.
      onsite_training_optin: onsiteOptin,
      // Each clause carries the initials the signer typed INTO THAT CLAUSE. They are read
      // per-key rather than broadcast from one value, so the record can show a clause was
      // initialled separately from the others.
      initials: initRows.reduce((acc, r) => ({ ...acc, [r.key]: initialsByClause[r.key] || '' }), {}),
      signature_image: signatureImage,
    };
    try {
      const res = await axios.post(`${API}/public/agreements/submit`, body);
      setReference(res.data?.reference || makeLocalRef());
      // A facility goes straight on to the card step; the link comes back with the signature.
      if (res.data?.card_setup_path) setCardSetupPath(res.data.card_setup_path);
      setStep(5);
    } catch (err) {
      setErrorMsg(
        err?.response?.data?.detail ||
          'We could not reach the server to record your signature. Your reference below is provisional — please contact Henry-Griffitts.'
      );
      setReference(makeLocalRef());
      setStep(5);
    } finally {
      setSubmitting(false);
    }
  };

  // ---- Review summary values ----
  const feeReviewLabel =
    feeChoice === 'annual' ? '$690 / year (annual)' : '$57.50 / month';
  const initialedCount = initRows.filter((r) => clauseInitialled(r.key)).length;

  const nextLabel = isSignStep ? (submitting ? 'Signing…' : 'Sign & complete') : 'Continue';
  const stepMeta = step < 5 ? `Step ${step + 1} of 5 — ${LABELS[step]}` : '';

  return (
    <div data-testid="fitter-signing-page">
      <style>{`
        .fs-root{
          --green:#48B64C; --green-d:#3A9B3E; --green-deep:#48B64C;
          --ink:#E8E8E8; --slate:#C2C2C2; --grey:#9A9A9A;
          --paper:#2A2A2A; --wash:#1E1E1E; --field:#242424; --line:#383838;
          /* THE ROOT FIX. This page is dark — --ink/--slate/--grey are LIGHT text. Eleven
             card surfaces hardcoded background:#fff, so light text landed on white:
             --slate on #fff was 1.78:1 and --ink 1.23:1, against a 4.5:1 requirement.
             Every card surface now uses --card. Hardcoding #fff on a surface that carries
             --ink/--slate text is the bug; use --card, --wash or --paper. The only
             deliberately light surface left is .sigpad, which is paper you sign on in dark
             ink, and its own text colours are set for that. --grey was #8C8C8C (4.27:1 on
             --paper, a fail); #9A9A9A clears 4.5:1 on all three surfaces. */
          --card:#242424;
          --amber:#E0B45C; --amber-bg:rgba(224,180,92,.12);
          --shadow:0 1px 2px rgba(0,0,0,.25),0 8px 30px rgba(0,0,0,.35);
          font-family:-apple-system,BlinkMacSystemFont,"Segoe UI",Roboto,Helvetica,Arial,sans-serif;
          background:
            radial-gradient(1200px 600px at 50% -200px,rgba(72,182,76,.05),transparent),
            #1E1E1E;
          color:var(--slate);line-height:1.55;-webkit-font-smoothing:antialiased;
          min-height:100vh;padding:0 16px 80px;
        }
        .fs-root *{box-sizing:border-box;margin:0;padding:0}
        @media (prefers-reduced-motion:reduce){.fs-root *{animation:none!important;transition:none!important}}
        .fs-root .topbar{max-width:760px;margin:0 auto;padding:22px 4px 18px;display:flex;align-items:center;justify-content:space-between;gap:16px}
        .fs-root .brand{display:flex;align-items:center;gap:11px}
        .fs-root .brand svg{display:block}
        /* The real tree is a TALL narrow mark (122x242 after trimming) where the old
           stand-in was near-square at 30x34. Height-constrained with width:auto so the
           aspect is never squashed; at 30px tall it is ~15px wide, which is NARROWER than
           what it replaced and so gives the header more room, not less. The asset ships at
           45x90 — 3x the display size, so it stays sharp on a phone. */
        .fs-root .treemark{display:block;height:30px;width:auto}
        .fs-root .brand .wm{font-weight:700;letter-spacing:.12em;font-size:13px;color:var(--ink);text-transform:uppercase}
        .fs-root .securetag{font-size:12px;color:var(--green-deep);background:rgba(72,182,76,.12);border:1px solid rgba(72,182,76,.25);padding:5px 11px;border-radius:100px;display:flex;align-items:center;gap:6px;font-weight:600;white-space:nowrap}
        .fs-root .securetag svg{display:block}
        .fs-root .progress{max-width:760px;margin:0 auto 18px;display:flex;gap:6px;padding:0 4px}
        .fs-root .pstep{flex:1;display:flex;flex-direction:column;gap:7px;cursor:pointer;border:none;background:none;text-align:left;font:inherit;padding:0}
        .fs-root .pbar{height:5px;border-radius:100px;background:var(--line);overflow:hidden;position:relative}
        .fs-root .pbar::after{content:"";position:absolute;inset:0;width:0;background:linear-gradient(90deg,var(--green),var(--green-d));border-radius:100px;transition:width .45s cubic-bezier(.4,0,.2,1)}
        .fs-root .pstep.done .pbar::after,.fs-root .pstep.active .pbar::after{width:100%}
        .fs-root .plabel{font-size:11.5px;font-weight:600;color:var(--grey);letter-spacing:.02em;display:flex;align-items:center;gap:5px;transition:color .3s}
        .fs-root .pstep.active .plabel,.fs-root .pstep.done .plabel{color:var(--green-deep)}
        .fs-root .pnum{width:17px;height:17px;border-radius:50%;border:1.5px solid var(--line);display:inline-flex;align-items:center;justify-content:center;font-size:10px;flex:none;transition:all .3s}
        .fs-root .pstep.active .pnum{background:var(--green);border-color:var(--green);color:#fff}
        .fs-root .pstep.done .pnum{background:var(--green-deep);border-color:var(--green-deep);color:#fff}
        @media(max-width:620px){.fs-root .plabel span{display:none}}
        .fs-root .sheet{max-width:760px;margin:0 auto;background:var(--paper);border:1px solid var(--line);border-radius:18px;box-shadow:var(--shadow);overflow:hidden}
        .fs-root .sheet-head{padding:30px 38px 24px;border-bottom:1px solid var(--line);background:var(--paper);position:relative}
        .fs-root .sheet-head::before{content:"";position:absolute;left:0;top:0;bottom:0;width:5px;background:linear-gradient(180deg,var(--green),var(--green-deep))}
        .fs-root .eyebrow{font-size:12px;font-weight:700;letter-spacing:.14em;text-transform:uppercase;color:var(--green);margin-bottom:8px}
        .fs-root .sheet-head h1{font-size:25px;color:var(--ink);font-weight:750;letter-spacing:-.01em;line-height:1.2}
        .fs-root .sheet-head p{margin-top:8px;color:var(--slate);font-size:14.5px;max-width:54ch}
        .fs-root .sheet-body{padding:30px 38px 34px}
        @media(max-width:560px){.fs-root .sheet-head,.fs-root .sheet-body{padding-left:22px;padding-right:22px}}
        .fs-root .step{display:none;animation:fs-fade .4s ease}
        .fs-root .step.show{display:block}
        @keyframes fs-fade{from{opacity:0;transform:translateY(8px)}to{opacity:1;transform:none}}
        .fs-root .grouptitle{font-size:13px;font-weight:700;letter-spacing:.04em;text-transform:uppercase;color:var(--green-deep);margin:26px 0 12px;padding-bottom:7px;border-bottom:1px solid var(--line)}
        .fs-root .grouptitle:first-child{margin-top:0}
        .fs-root .fgrid{display:grid;grid-template-columns:1fr 1fr;gap:13px 16px}
        .fs-root .f{display:flex;flex-direction:column;gap:5px}
        .fs-root .f.full{grid-column:1/-1}
        @media(max-width:560px){.fs-root .fgrid{grid-template-columns:1fr}.fs-root .f.two{grid-column:1/-1}}
        .fs-root label{font-size:12.5px;font-weight:600;color:var(--ink)}
        .fs-root label .opt{color:var(--grey);font-weight:500}
        .fs-root input[type=text],.fs-root input[type=email],.fs-root input[type=tel],.fs-root select{font:inherit;font-size:14.5px;color:var(--ink);background:var(--field);border:1px solid #EADFBE;border-radius:9px;padding:10px 12px;outline:none;transition:border-color .2s,box-shadow .2s;width:100%}
        .fs-root input:focus,.fs-root select:focus{border-color:var(--green);box-shadow:0 0 0 3px rgba(72,182,76,.15)}
        .fs-root .radio-row{display:flex;gap:10px;flex-wrap:wrap}
        .fs-root .pill{display:inline-flex;align-items:center;gap:7px;font-size:13.5px;font-weight:600;color:var(--slate);background:var(--card);border:1px solid var(--line);padding:8px 14px;border-radius:100px;cursor:pointer;transition:all .2s;position:relative}
        .fs-root .pill input{position:absolute;opacity:0;pointer-events:none}
        .fs-root .pill:has(input:checked){border-color:var(--green);background:var(--wash);color:var(--green-deep)}
        .fs-root .pill .dot{width:14px;height:14px;border-radius:50%;border:2px solid var(--line);transition:all .2s;flex:none}
        .fs-root .pill:has(input:checked) .dot{border-color:var(--green);background:var(--green);box-shadow:inset 0 0 0 2.5px #fff}
        .fs-root .feecards{display:grid;grid-template-columns:1fr 1fr;gap:14px;margin-top:6px}
        @media(max-width:560px){.fs-root .feecards{grid-template-columns:1fr}}
        .fs-root .feecard{position:relative;border:1.5px solid var(--line);border-radius:14px;padding:18px 18px 16px;cursor:pointer;background:var(--card);transition:all .22s}
        .fs-root .feecard:hover{border-color:#C7D9B6}
        .fs-root .feecard:has(input:checked){border-color:var(--green);background:var(--wash);box-shadow:0 4px 18px rgba(72,182,76,.13)}
        .fs-root .feecard input{position:absolute;opacity:0}
        .fs-root .feecard .tag{position:absolute;top:14px;right:14px;font-size:10.5px;font-weight:700;letter-spacing:.06em;text-transform:uppercase;color:var(--green-deep);background:rgba(72,182,76,.14);padding:3px 8px;border-radius:100px}
        .fs-root .feecard .cad{font-size:13px;font-weight:700;color:var(--ink);display:flex;align-items:center;gap:8px}
        .fs-root .feecard .cad .check{width:18px;height:18px;border-radius:50%;border:2px solid var(--line);flex:none;display:inline-flex;align-items:center;justify-content:center;transition:all .2s}
        .fs-root .feecard:has(input:checked) .cad .check{background:var(--green);border-color:var(--green)}
        .fs-root .feecard:has(input:checked) .cad .check svg{opacity:1}
        .fs-root .feecard .cad .check svg{opacity:0;transition:opacity .2s}
        .fs-root .feecard .amt{font-size:27px;font-weight:780;color:var(--ink);margin:10px 0 2px;letter-spacing:-.02em}
        .fs-root .feecard .amt small{font-size:14px;font-weight:600;color:var(--grey)}
        .fs-root .feecard .note{font-size:12px;color:var(--slate);line-height:1.45}
        .fs-root .consent{display:flex;gap:13px;align-items:flex-start;border:1px solid var(--line);border-radius:13px;padding:15px 16px;margin-top:13px;background:var(--card);transition:all .2s;position:relative}
        .fs-root .consent:has(input:checked){border-color:var(--green);background:var(--wash)}
        .fs-root .chk{flex:none;width:22px;height:22px;border-radius:7px;border:2px solid #7C8A74;background:var(--wash);cursor:pointer;display:inline-flex;align-items:center;justify-content:center;margin-top:1px;transition:all .2s}
        .fs-root .chk svg{opacity:0;transform:scale(.6);transition:all .2s}
        .fs-root .consent:has(input:checked) .chk{background:var(--green);border-color:var(--green)}
        .fs-root .consent:has(input:checked) .chk svg{opacity:1;transform:scale(1)}
        .fs-root .consent input{position:absolute;opacity:0;width:0;height:0}
        .fs-root .consent .ctext{font-size:13.5px;color:var(--slate)}
        .fs-root .consent .ctext b{color:var(--ink);font-weight:650;display:block;margin-bottom:2px;font-size:14px}
        .fs-root .terms{font-size:14px;color:var(--slate)}
        .fs-root .terms h3{font-size:14px;font-weight:700;color:var(--green-deep);margin:22px 0 9px;display:flex;align-items:baseline;gap:9px}
        .fs-root .terms h3:first-child{margin-top:0}
        .fs-root .terms h3 .s{color:var(--green);font-variant-numeric:tabular-nums}
        .fs-root .clause{margin:0 0 11px;padding-left:2px}
        .fs-root .clause b{color:var(--ink);font-weight:650}
        .fs-root .clause.bullet{position:relative;padding-left:18px}
        .fs-root .clause.bullet::before{content:"•";position:absolute;left:5px;color:var(--green);font-weight:700}
        .fs-root .scrollbox{max-height:300px;overflow-y:auto;border:1px solid var(--line);border-radius:13px;padding:20px 22px;background:var(--card)}
        .fs-root .scrollbox::-webkit-scrollbar{width:9px}
        .fs-root .scrollbox::-webkit-scrollbar-thumb{background:#4E5A48;border-radius:100px;border:2px solid var(--card)}
        .fs-root .readnote{font-size:12.5px;color:var(--grey);margin-top:9px;display:flex;align-items:center;gap:7px}
        .fs-root .readnote.ok{color:var(--green-deep);font-weight:600}
        .fs-root .keybox{border:1.5px solid var(--green);border-left:6px solid var(--green);border-radius:14px;background:linear-gradient(180deg,rgba(72,182,76,.10),var(--card));padding:20px 22px;margin:6px 0 4px}
        .fs-root .keybox .kh{display:flex;align-items:center;gap:9px;font-size:14px;font-weight:750;color:var(--green-deep);margin-bottom:13px}
        .fs-root .keybox .kh .badge{font-size:10.5px;background:var(--green);color:#fff;padding:2px 8px;border-radius:100px;letter-spacing:.05em;text-transform:uppercase;font-weight:700}
        .fs-root .kitem{display:flex;gap:11px;margin-bottom:11px;font-size:13.5px}
        .fs-root .kitem:last-child{margin-bottom:0}
        .fs-root .kitem .kn{flex:none;width:22px;height:22px;border-radius:7px;background:var(--wash);border:1.5px solid var(--green);color:var(--green);font-weight:700;font-size:12px;display:flex;align-items:center;justify-content:center}
        .fs-root .kitem b{color:var(--ink)}
        .fs-root .big{color:var(--green-deep);font-weight:800}
        .fs-root .initrow{display:flex;align-items:center;justify-content:space-between;gap:14px;border:1px solid var(--line);border-radius:12px;padding:13px 15px;margin-top:11px;background:var(--card);transition:all .25s}
        .fs-root .initrow.signed{border-color:var(--green);background:var(--wash)}
        .fs-root .initrow .it{font-size:13.5px;color:var(--slate)}
        .fs-root .initrow .it b{color:var(--ink);font-weight:650}
        /* Typed initials box. Deliberately a plain, obviously-editable field: the previous
           control was a button that stamped a generated value, which read as "already done".
           No script face here either — that was decoration on something that is evidence.

           SELECTOR NOTE — this is written as '.initrow input.initbox', not '.initbox', and
           the extra weight is load-bearing. The generic field rule above is
           '.fs-root input[type=text]{width:100%}' (0,2,1), which OUTRANKS a plain
           '.fs-root .initbox' (0,2,0). When the control became a real <input>, it silently
           took width:100% — so on desktop the box stretched to ~650px and squeezed the
           clause label into a ~50px column breaking one word per line. Mobile looked fine
           only because the <=420px rule stacks the row and makes the box full-width by
           intent. Keep this selector at least as specific as the generic input rule. */
        .fs-root .initrow input.initbox{flex:0 0 112px;width:112px;font:inherit;font-size:15px;font-weight:700;letter-spacing:.08em;text-align:center;text-transform:uppercase;border:1.5px dashed var(--green);color:var(--ink);background:var(--field);border-radius:10px;padding:10px 12px;transition:all .2s;min-height:44px}
        /* The label takes the rest of the row and is allowed to wrap normally. */
        .fs-root .initrow .it{flex:1 1 auto;min-width:0}
        .fs-root .initbox::placeholder{font-size:12px;font-weight:600;letter-spacing:0;text-transform:none;color:var(--grey)}
        .fs-root .initrow input.initbox:focus{outline:none;border-style:solid;border-color:var(--green);box-shadow:0 0 0 3px rgba(72,182,76,.22)}
        .fs-root .initrow.signed .initbox{border-style:solid;background:var(--green);color:#fff;border-color:var(--green)}
        .fs-root .initnote{font-size:12.5px;color:var(--grey);margin:12px 2px 0}
        .fs-root .sigwrap{border:1px solid var(--line);border-radius:14px;overflow:hidden;background:var(--card);margin-top:8px}
        .fs-root .sigpad{position:relative;background:repeating-linear-gradient(180deg,#FDFDFB,#FDFDFB 38px,#EFEFE9 38px,#EFEFE9 39px);height:140px;cursor:crosshair}
        .fs-root .sigpad canvas{display:block;width:100%;height:100%;touch-action:none}
        .fs-root .sigplace{position:absolute;left:22px;top:50%;transform:translateY(-50%);color:#5A6552;font-size:15px;font-style:italic;pointer-events:none;transition:opacity .2s}
        .fs-root .sigplace.hide{opacity:0}
        .fs-root .sigx{position:absolute;left:22px;bottom:20px;color:#5A6552;font-size:20px;pointer-events:none;font-weight:300}
        .fs-root .sigbar{display:flex;align-items:center;justify-content:space-between;padding:11px 16px;border-top:1px solid var(--line);background:var(--field)}
        .fs-root .sigbar .lab{font-size:12px;color:var(--grey)}
        .fs-root .clearbtn{font:inherit;font-size:12.5px;font-weight:600;color:var(--slate);background:none;border:none;cursor:pointer;text-decoration:underline;text-underline-offset:2px}
        .fs-root .navbar{display:flex;align-items:center;justify-content:space-between;gap:14px;margin-top:28px;padding-top:22px;border-top:1px solid var(--line)}
        .fs-root .btn{font:inherit;font-size:14.5px;font-weight:650;cursor:pointer;border-radius:11px;padding:12px 24px;transition:all .2s;border:1px solid transparent}
        .fs-root .btn-ghost{background:none;color:var(--slate);border-color:var(--line)}
        .fs-root .btn-ghost:hover{background:#333333}
        .fs-root .btn-ghost:disabled{opacity:0;pointer-events:none}
        .fs-root .btn-primary{background:linear-gradient(180deg,var(--green),var(--green-d));color:#fff;box-shadow:0 4px 14px rgba(74,140,40,.3)}
        .fs-root .btn-primary:hover{transform:translateY(-1px);box-shadow:0 6px 18px rgba(74,140,40,.36)}
        .fs-root .btn-primary:disabled{background:#CBD3C2;box-shadow:none;cursor:not-allowed;transform:none;color:#fff}
        .fs-root .stepmeta{font-size:12.5px;color:var(--grey);text-align:center;flex:1}
        .fs-root .rev{display:grid;gap:11px}
        .fs-root .revrow{display:flex;justify-content:space-between;gap:16px;padding:13px 16px;border:1px solid var(--line);border-radius:11px;background:var(--card);font-size:13.5px}
        .fs-root .revrow .k{color:var(--grey);font-weight:600}
        .fs-root .revrow .v{color:var(--ink);font-weight:600;text-align:right}
        .fs-root .revrow .v.ok{color:var(--green-deep);display:flex;align-items:center;gap:6px;justify-content:flex-end}
        .fs-root .done{text-align:center;padding:18px 0 8px}
        .fs-root .done .seal{width:88px;height:88px;margin:0 auto 22px;border-radius:50%;background:linear-gradient(135deg,var(--green),var(--green-deep));display:flex;align-items:center;justify-content:center;box-shadow:0 10px 30px rgba(74,140,40,.35);animation:fs-pop .5s cubic-bezier(.2,1.3,.4,1)}
        @keyframes fs-pop{from{transform:scale(.5);opacity:0}to{transform:scale(1);opacity:1}}
        .fs-root .done h2{font-size:23px;color:var(--ink);font-weight:750;margin-bottom:8px}
        .fs-root .done p{color:var(--slate);font-size:14.5px;max-width:46ch;margin:0 auto 6px}
        .fs-root .done .ref{margin-top:18px;display:inline-block;font-size:12.5px;color:var(--grey);background:#333333;border:1px solid var(--line);padding:8px 16px;border-radius:100px;font-variant-numeric:tabular-nums}
        .fs-root .errnote{margin:14px auto 0;max-width:760px;font-size:13px;color:#F08A80;background:rgba(224,82,82,.12);border:1px solid rgba(224,82,82,.4);border-radius:10px;padding:10px 14px}
        /* "What happens next" + a real ending. The completion screen used to dead-end. */
        .fs-root .whatnext{text-align:left;max-width:520px;margin:24px auto 0;border:1px solid var(--line);border-radius:14px;background:var(--wash);padding:18px 20px}
        .fs-root .whatnext .wnh{font-size:12px;font-weight:700;letter-spacing:.06em;text-transform:uppercase;color:var(--green-deep);margin-bottom:12px}
        .fs-root .whatnext ol{margin:0;padding-left:20px;display:flex;flex-direction:column;gap:11px}
        .fs-root .whatnext li{font-size:13.5px;color:var(--slate);line-height:1.5}
        .fs-root .whatnext li b{color:var(--ink);font-weight:650}
        .fs-root .whatnext .wnfoot{font-size:13px;color:var(--slate);margin:14px 0 0;max-width:none;border-top:1px solid var(--line);padding-top:13px}
        .fs-root .doneactions{margin-top:24px;display:flex;flex-direction:column;align-items:center;gap:12px}
        .fs-root .doneactions .btn{min-width:200px;text-align:center;text-decoration:none;display:inline-flex;align-items:center;justify-content:center}
        .fs-root .donelink{font-size:13px;color:var(--grey)}

        /* ---- item 3: required authorisations, presented as statements ------------------
           Deliberately NOT shaped like .consent. The optional onsite-training checkbox above
           is a real choice and keeps its box; these are what signing authorises, so they get
           a Required tag, a distinct panel, and no control to tick. */
        .fs-root .reqtag{display:inline-block;margin-left:8px;font-size:10.5px;font-weight:700;
          letter-spacing:.06em;text-transform:uppercase;color:#1E1E1E;background:var(--amber);
          padding:2px 8px;border-radius:100px;vertical-align:middle}
        .fs-root .authbox{border:1px solid var(--amber);border-left:5px solid var(--amber);
          border-radius:13px;background:var(--amber-bg);padding:16px 18px}
        .fs-root .authlead{font-size:13px;color:var(--ink);margin-bottom:13px;font-weight:600}
        .fs-root .authitem{display:flex;gap:12px;align-items:flex-start;margin-bottom:12px;
          font-size:13.5px;color:var(--slate);line-height:1.55}
        .fs-root .authitem b{color:var(--ink);font-weight:650}
        .fs-root .authmark{flex:none;width:21px;height:21px;border-radius:6px;background:var(--green);
          display:inline-flex;align-items:center;justify-content:center;margin-top:1px}
        .fs-root .authfoot{font-size:12.5px;color:var(--grey);margin:0;padding-top:11px;
          border-top:1px solid rgba(224,180,92,.3)}

        /* ---- the display face is for the WORDMARK ONLY --------------------------------
           Restricting 'BN 3rd Place' to the two large headings was the previous attempt and
           it was not enough: "fees & authorizations", "license terms", "initial the key
           clauses" and "agreement signed" ARE those headings, and they were still unreadable.
           Every heading now inherits the body stack. The wordmark keeps the face because
           there it is a logotype rather than a sentence. */
        .fs-root h1,.fs-root h2,.fs-root h3,.fs-root h4,.fs-root h5,.fs-root h6{
          font-family:inherit;letter-spacing:normal}
        /* The wordmark opts in via .hg-wordmark (index.css) — one rule, both public pages. */

        /* ---- item 7: nothing in the terms is tappable, so nothing may look it ----------
           The clauses were never expandable — there is no hidden detail behind them. An
           element that looks tappable and does nothing is worse than plain text, so the
           affordance is removed and the box says outright that nothing is collapsed. */
        .fs-root .terms h3,.fs-root .clause,.fs-root .kitem{cursor:default}
        .fs-root .terms h3:hover,.fs-root .clause:hover{background:none}
        .fs-root .termsnote{font-size:12.5px;color:var(--grey);margin:0 2px 10px}

        /* ---- item 11: phone widths ---------------------------------------------------
           Randall ran the whole flow on a phone and so will his fitters. */
        /* iOS Safari zooms the whole page when a focused input is under 16px. Every field
           was 14.5px, so tapping any of them zoomed and left the layout scrolled sideways. */
        @media(max-width:560px){
          .fs-root input[type=text],.fs-root input[type=email],.fs-root input[type=tel],
          .fs-root select{font-size:16px}
          .fs-root .initrow input.initbox{font-size:16px}
        }
        /* The wordmark and the "Secure signing" pill collided: both were on one nowrap row
           with the wordmark tracked out at .12em. */
        @media(max-width:480px){
          .fs-root .topbar{flex-wrap:wrap;gap:8px;padding-top:16px}
          .fs-root .brand .wm{font-size:11.5px;letter-spacing:.06em}
          .fs-root .securetag{font-size:11px;padding:4px 9px}
          .fs-root .treemark{height:26px}
        }
        /* The progress bar wrapped onto two rows: five flex children whose content set a
           min-width they could not shrink below. */
        .fs-root .progress{flex-wrap:nowrap}
        .fs-root .pstep{min-width:0}
        .fs-root .plabel{min-width:0;overflow:hidden;text-overflow:ellipsis;white-space:nowrap}
        /* An initials row is a label plus a 112px box; below ~420px the label was crushed
           to two characters per line. */
        @media(max-width:420px){
          .fs-root .initrow{flex-wrap:wrap;gap:10px}
          .fs-root .initrow input.initbox{flex:1 1 100%;width:100%}
          .fs-root .revrow{flex-direction:column;gap:4px}
          .fs-root .revrow .v{text-align:left}
        }
        /* Tap targets: the nav buttons and the clear-signature control were below the 44px
           minimum on a phone. */
        @media(max-width:560px){
          .fs-root .btn{min-height:46px}
          .fs-root .clearbtn{min-height:44px;padding-left:16px;padding-right:16px}
          .fs-root .navbar{gap:10px}
          .fs-root .stepmeta{display:none}
        }
        .fs-root .fieldnote{font-size:12px;color:#C9D2BE;margin-top:6px;line-height:1.45}
        .fs-root .hint{font-size:12px;color:var(--grey);text-align:center;max-width:760px;margin:18px auto 0;display:flex;align-items:center;justify-content:center;gap:7px}
      `}</style>

      <div className="fs-root">
        <div className="topbar">
          <div className="brand">
            {/* The real Henry-Griffitts tree mark, colour-isolated from the wordmark. The
                shape it replaces was a hand-drawn stand-in: not our tree, and drawn for a
                light page that no longer exists. */}
            <img src={hgTree} alt="" aria-hidden="true" className="treemark" />
            <span className="wm hg-wordmark">Henry&#8209;Griffitts</span>
          </div>
          <div className="securetag">
            <svg width="12" height="13" viewBox="0 0 12 13" fill="none">
              <path d="M6 1l4 1.6v3.2C10 9 8.2 11 6 12 3.8 11 2 9 2 5.8V2.6L6 1z" stroke="#48B64C" strokeWidth="1.3" strokeLinejoin="round" />
            </svg>
            Secure signing
          </div>
        </div>

        {inviteState !== 'valid' ? (
          <main className="sheet">
            <div className="sheet-head">
              <div className="eyebrow">Secure signing</div>
              <h1>{inviteState === 'loading' ? 'Opening your signing link…' : 'Signing link not valid'}</h1>
              <p>
                {inviteState === 'loading'
                  ? 'One moment while we load your agreement.'
                  : 'This signing link is invalid or has already been used. Please contact Henry-Griffitts (hgsupport@henry-griffitts.com) and we’ll send you a fresh link.'}
              </p>
            </div>
          </main>
        ) : (
        <>
        <nav className="progress" aria-label="Progress">
          {LABELS.map((lab, i) => {
            const cls = i === step ? 'active' : i < step ? 'done' : '';
            return (
              <button
                key={lab}
                type="button"
                className={`pstep ${cls}`.trim()}
                onClick={() => jumpToStep(i)}
                disabled={i > step}
              >
                <div className="pbar" />
                <span className="plabel"><span className="pnum">{i + 1}</span><span>{lab}</span></span>
              </button>
            );
          })}
        </nav>

        <main className="sheet">
          <div className="sheet-head">
            <div className="eyebrow">{step < 5 ? `Step ${step + 1} of 5` : 'Complete'}</div>
            <h1>{step < 5 ? (isFacility ? FACILITY_STEPS : STEPS)[step].title : 'Agreement signed'}</h1>
            <p>{step < 5 ? (isFacility ? FACILITY_STEPS : STEPS)[step].sub : (isFacility ? 'Signed. One more step: your card on file.' : 'Signed and with our team for review.')}</p>
          </div>

          <div className="sheet-body">
            {/* STEP 0 — Account information */}
            <section className={`step${step === 0 ? ' show' : ''}`} data-testid="fs-step-0" data-step="0">
              {isFacility ? (
                <>
                  <div className="grouptitle">Facility</div>
                  <div className="fgrid" data-testid="fs-facility-fields">
                    <div className="f full"><label>Facility legal name</label><input type="text" data-testid="fs-legal-name" value={bizName} onChange={(e) => setBizName(e.target.value)} /></div>
                    <div className="f full"><label>Doing business as <span className="opt">(if different)</span></label><input type="text" value={dba} onChange={(e) => setDba(e.target.value)} /></div>
                    <div className="f full"><label>Facility type</label>
                      <select data-testid="fs-facility-type" value={facilityType} onChange={(e) => setFacilityType(e.target.value)}>
                        <option value="">Select…</option>
                        {facilityTypes.map((t) => <option key={t} value={t}>{t}</option>)}
                      </select>
                    </div>
                    <div className="f two"><label>Owner / authorized signer</label><input type="text" placeholder="Full name" value={contactName} onChange={(e) => setContactName(e.target.value)} /></div>
                    <div className="f two"><label>Title</label><input type="text" placeholder="e.g. Owner" value={signerTitle} onChange={(e) => setSignerTitle(e.target.value)} /></div>
                    <div className="f two"><label>Email <span className="opt">(from your invite)</span></label><input type="email" value={email} readOnly style={{ background: '#202020', color: 'var(--grey)' }} /></div>
                    <div className="f two"><label>Phone</label><input type="tel" placeholder="(000) 000-0000" value={phone} onChange={(e) => setPhone(e.target.value)} /></div>
                  </div>
                  <div className="grouptitle">Designated Fitter (coach)</div>
                  <div className="fgrid">
                    <div className="f full"><label>Name</label><input type="text" data-testid="fs-fitter-name" value={fitterName} onChange={(e) => setFitterName(e.target.value)} /></div>
                    <div className="f two"><label>Email</label><input type="email" value={fitterEmail} onChange={(e) => setFitterEmail(e.target.value)} /></div>
                    <div className="f two"><label>Phone</label><input type="tel" value={fitterPhone} onChange={(e) => setFitterPhone(e.target.value)} /></div>
                  </div>
                </>
              ) : (
              <>
              <div className="grouptitle">Account &amp; contact</div>
              <div className="fgrid">
                <div className="f full"><label>Account / business name</label><input type="text" placeholder="e.g. KC Golf Swing" value={bizName} onChange={(e) => setBizName(e.target.value)} /></div>
                <div className="f two"><label>Contact name</label><input type="text" placeholder="Full name" value={contactName} onChange={(e) => setContactName(e.target.value)} /></div>
                <div className="f two"><label>Cell phone</label><input type="tel" placeholder="(000) 000-0000" value={phone} onChange={(e) => setPhone(e.target.value)} /></div>
                <div className="f two"><label>Email <span className="opt">(from your invite)</span></label><input type="email" placeholder="you@example.com" value={email} readOnly style={{ background: '#202020', color: 'var(--grey)' }} /></div>
                <div className="f two"><label>Course or club</label><input type="text" placeholder="Name of course" value={courseOrClub} onChange={(e) => setCourseOrClub(e.target.value)} /></div>
                <div className="f full"><label>Authorized fitter(s) at this location</label><input type="text" placeholder="Fitter name(s)" value={authorizedFitters} onChange={(e) => setAuthorizedFitters(e.target.value)} /></div>
              </div>
              </>
              )}
              {/* SHIPPING, and it says so. One block labelled "Physical address" had to serve
                  as both the delivery address for the demo system and the billing address for
                  the card — and for a fitter at a golf club those are routinely different. */}
              <div className="grouptitle">{isFacility ? 'Facility address' : 'Shipping address'}</div>
              <p style={{ fontSize: '12.5px', margin: '-6px 0 12px', color: 'var(--grey)' }}>
                {isFacility
                  ? 'Where the fitting system is set up — and the address your market is measured from.'
                  : 'Where your demo system and everything else should be delivered.'}
              </p>
              <div className="fgrid">
                <div className="f full"><label>Street address</label><input type="text" value={addressLine1} onChange={(e) => setAddressLine1(e.target.value)} /></div>
                <div className="f"><label>City</label><input type="text" value={city} onChange={(e) => setCity(e.target.value)} /></div>
                {/* "State / Country" was the label until 2026-08-14, with a Country dropdown
                    directly beneath it. Answered literally, it produced state="United States"
                    on HG-WPCWM-26 — so the signed PDF printed the country twice and no state
                    at all. The field only ever wanted the sub-national part. */}
                <div className="f"><label>State / Province</label><input type="text" placeholder="e.g. ID" value={stateField} onChange={(e) => setStateField(e.target.value)} /></div>
                <div className="f"><label>Zip / postal code</label><input type="text" value={postalCode} onChange={(e) => setPostalCode(e.target.value)} /></div>
                <div className="f"><label>Country</label>
                  <select data-testid="fs-country" value={country} onChange={(e) => setCountry(e.target.value)}>
                    <option value="">Select your country…</option>
                    {countryOptions.map((o) => <option key={o.value} value={o.value}>{o.label}</option>)}
                  </select>
                  {country === countryOther && (
                    <div className="fieldnote" data-testid="fs-country-other-note">
                      No problem — we'll set your region up by hand and send the right price list.
                    </div>
                  )}
                </div>
              </div>

              <div className="grouptitle">Billing address</div>
              <label className="consent" data-testid="fs-billing-same">
                <input
                  type="checkbox"
                  checked={billingSame}
                  onChange={(e) => setBillingSame(e.target.checked)}
                />
                <span className="chk"><svg width="13" height="13" viewBox="0 0 13 13"><path d="M2.5 6.8l2.6 2.6L10.5 4" stroke="#fff" strokeWidth="2" fill="none" strokeLinecap="round" strokeLinejoin="round" /></svg></span>
                <span className="ctext"><b>Billing address is the same as shipping</b>Untick if the card
                  you'll put on file bills to a different address — a club's accounts office, for
                  example.</span>
              </label>
              {!billingSame && (
                <div className="fgrid" data-testid="fs-billing-fields" style={{ marginTop: '13px' }}>
                  <div className="f full"><label>Billing street address</label><input type="text" data-testid="fs-billing-line1" value={billingLine1} onChange={(e) => setBillingLine1(e.target.value)} /></div>
                  <div className="f"><label>City</label><input type="text" value={billingCity} onChange={(e) => setBillingCity(e.target.value)} /></div>
                  <div className="f"><label>State / Province</label><input type="text" value={billingState} onChange={(e) => setBillingState(e.target.value)} /></div>
                  <div className="f"><label>Zip / postal code</label><input type="text" value={billingPostal} onChange={(e) => setBillingPostal(e.target.value)} /></div>
                  <div className="f"><label>Country</label><input type="text" value={billingCountry} onChange={(e) => setBillingCountry(e.target.value)} /></div>
                  <div className="f full">
                    <div className="fieldnote">
                      Your price list still follows your <b>shipping</b> address — that's where you fit.
                    </div>
                  </div>
                </div>
              )}
              {!isFacility && (<>
              <div className="grouptitle">Business type &amp; referrals</div>
              <div className="fgrid">
                <div className="f"><label>Business type</label>
                  <select value={businessType} onChange={(e) => setBusinessType(e.target.value)}>
                    <option value="">Select…</option>
                    <option>Corporation</option>
                    <option>Sole proprietor</option>
                    <option>Other</option>
                  </select>
                </div>
                <div className="f"><label>Can you accept outside referrals?</label>
                  <div className="radio-row" style={{ marginTop: '4px' }}>
                    <label className="pill"><span className="dot" /><input type="radio" name="ref" checked={acceptsReferrals === 'yes'} onChange={() => setAcceptsReferrals('yes')} />Yes</label>
                    <label className="pill"><span className="dot" /><input type="radio" name="ref" checked={acceptsReferrals === 'no'} onChange={() => setAcceptsReferrals('no')} />No</label>
                  </div>
                </div>
              </div>
              </>)}
            </section>

            {/* STEP 1 — Fees & authorizations */}
            <section className={`step${step === 1 ? ' show' : ''}`} data-testid="fs-step-1" data-step="1">
              {isFacility && (
                <>
                  <div className="grouptitle">Start-up fee</div>
                  <div className="feecards" style={{ gridTemplateColumns: '1fr' }} data-testid="fs-startup-fee">
                    <div className="feecard" style={{ cursor: 'default', borderColor: 'var(--green)', background: 'var(--wash)' }}>
                      <span className="tag">Due at signing</span>
                      <span className="cad">One time</span>
                      <div className="amt">{fees?.start_up || '$1,500'}</div>
                      <div className="note">Non-refundable. Covers the launch package: the fitting system shipped and set up, your coach's certification, the launch fit day, the co-branded marketing kit and custom signage (sections 3.1 and 4.1).</div>
                    </div>
                  </div>
                </>
              )}
              <div className="grouptitle">License fee — choose one</div>
              <p style={{ fontSize: '13.5px', margin: '-4px 0 14px', color: 'var(--slate)' }}>{isFacility
                ? 'The standard license fee, starting on the Effective Date — the day both parties have signed. Pay monthly, or prepay for the year. No minimum order in your first quarter.'
                : <>Your license includes a monthly support &amp; maintenance fee. Pay monthly, or prepay for the year.</>}</p>
              <div className="feecards">
                <label className="feecard">
                  <input type="radio" name="fee" value="monthly" checked={feeChoice === 'monthly'} onChange={() => setFeeChoice('monthly')} />
                  <span className="tag">Standard</span>
                  <span className="cad"><span className="check"><svg width="11" height="11" viewBox="0 0 11 11"><path d="M2 5.6l2.2 2.2L9 3" stroke="#fff" strokeWidth="1.8" fill="none" strokeLinecap="round" strokeLinejoin="round" /></svg></span>Monthly</span>
                  <div className="amt">$57.50<small>/mo</small></div>
                  <div className="note">Billed monthly to the card on file.</div>
                </label>
                <label className="feecard">
                  <input type="radio" name="fee" value="annual" checked={feeChoice === 'annual'} onChange={() => setFeeChoice('annual')} />
                  <span className="cad"><span className="check"><svg width="11" height="11" viewBox="0 0 11 11"><path d="M2 5.6l2.2 2.2L9 3" stroke="#fff" strokeWidth="1.8" fill="none" strokeLinecap="round" strokeLinejoin="round" /></svg></span>Annual</span>
                  <div className="amt">$690<small>/yr</small></div>
                  <div className="note">One charge (12 × $57.50). Non-refundable, not prorated if the license ends mid-year.</div>
                </label>
              </div>
              {isFacility ? (
                <>
                  <div className="grouptitle">
                    Card on file <span className="reqtag" data-testid="fs-auth-required">Required</span>
                  </div>
                  <div className="authbox" data-testid="fs-facility-authorization">
                    <p className="authlead">Signing this agreement authorizes the following (section 3.5):</p>
                    <div className="authitem">
                      <span className="authmark" aria-hidden="true">
                        <svg width="13" height="13" viewBox="0 0 13 13"><path d="M2.5 6.8l2.6 2.6L10.5 4" stroke="#fff" strokeWidth="2" fill="none" strokeLinecap="round" strokeLinejoin="round" /></svg>
                      </span>
                      <span><b>You authorize us to charge the card on file for amounts due under this Agreement.</b> Straight
                        after you sign, you'll add the card on our payment processor — Henry-Griffitts never sees or stores
                        the number.</span>
                    </div>
                    <p className="authfoot">If you'd rather not authorize this, don't sign — talk to us first
                      and we'll go through it with you.</p>
                  </div>
                </>
              ) : (<>
              <div className="grouptitle">Optional onsite training</div>
              <p style={{ fontSize: '13.5px', margin: '-4px 0 12px', color: 'var(--slate)' }}>
                Your license includes <b>remote training</b> and our <b>online education portal</b> — that's the standard, and it's included. If you'd also like an in-person onsite training visit &amp; event, you can add one: <b>$1,500 flat in the lower 48 states</b> (covers our travel), or <b>$1,500 plus travel</b> outside the lower 48. This is optional — you can also request it anytime later.
              </p>
              <label className="consent" data-testid="fs-onsite-optin">
                <input type="checkbox" checked={onsiteOptin} onChange={(e) => setOnsiteOptin(e.target.checked)} />
                <span className="chk"><svg width="13" height="13" viewBox="0 0 13 13"><path d="M2.5 6.8l2.6 2.6L10.5 4" stroke="#fff" strokeWidth="2" fill="none" strokeLinecap="round" strokeLinejoin="round" /></svg></span>
                <span className="ctext"><b>Yes, add an onsite training visit &amp; event ($1,500; +travel outside the lower 48)</b>We'll coordinate scheduling and billing after signup. Leave unchecked to stick with the standard remote + online portal training (no extra charge).</span>
              </label>
              {/* ITEM 3 — required authorisations are STATEMENTS, not checkboxes.
                  These were two boxes identical in appearance to the optional one above,
                  implying a choice that did not exist: the terms assume both are granted, and
                  canContinue() never enforced them anyway, so a fitter could untick both and
                  complete. Presenting them as what signing authorises removes the false
                  choice AND the unenforced gate in one move. The server derives the recorded
                  consent from the signature (agreements_api.process_submission), so there is
                  no client-sent boolean left to disagree with what the fitter actually did. */}
              <div className="grouptitle">
                Authorizations <span className="reqtag" data-testid="fs-auth-required">Required</span>
              </div>
              <div className="authbox" data-testid="fs-authorizations">
                <p className="authlead">Signing this agreement authorizes the following. They are part of the
                  agreement, not optional extras:</p>
                <div className="authitem">
                  <span className="authmark" aria-hidden="true">
                    <svg width="13" height="13" viewBox="0 0 13 13"><path d="M2.5 6.8l2.6 2.6L10.5 4" stroke="#fff" strokeWidth="2" fill="none" strokeLinecap="round" strokeLinejoin="round" /></svg>
                  </span>
                  <span><b>Card on file.</b> Henry-Griffitts may keep a card on file and charge it for
                    the license fee and for orders at the time of order. After signing you'll get a
                    secure email to enter your card — Henry-Griffitts never sees or stores the number.</span>
                </div>
                <div className="authitem">
                  <span className="authmark" aria-hidden="true">
                    <svg width="13" height="13" viewBox="0 0 13 13"><path d="M2.5 6.8l2.6 2.6L10.5 4" stroke="#fff" strokeWidth="2" fill="none" strokeLinecap="round" strokeLinejoin="round" /></svg>
                  </span>
                  <span><b>Background / credit check.</b> Henry-Griffitts may obtain a background and/or
                    credit check in connection with this account.</span>
                </div>
                <p className="authfoot">If you'd rather not authorize these, don't sign — talk to us first
                  and we'll go through it with you.</p>
              </div>
              </>)}
            </section>

            {/* STEP 2 — License terms */}
            <section className={`step${step === 2 ? ' show' : ''}`} data-testid="fs-step-2" data-step="2">
              <p className="termsnote">The full terms are below — scroll through to the end. Nothing here is collapsed, so there is nothing to tap open.</p>
              {/* Rendered from the terms the backend sent — the SAME list the signed PDF
                  renders. Previously these clauses were written out here in JSX and the PDF
                  carried only a summary, so the licensee's counterpart did not contain the
                  terms they had agreed to. Do not re-inline the text; edit
                  backend/agreement_terms.py. */}
              <div className="scrollbox terms" ref={termsRef} onScroll={(e) => termsScrolled(e.currentTarget)}>
                {terms?.preamble && (
                  <p className="clause" data-testid="fs-terms-preamble">{richText(terms.preamble, 'pre')}</p>
                )}
                {(terms?.sections || []).map((sec) => (
                  <div key={sec.n}>
                    <h3><span className="s">{sec.n}</span> {sec.title}</h3>
                    {(sec.clauses || []).map((cl, ci) => (
                      <p className={`clause${cl.bullet ? ' bullet' : ''}`} key={`${sec.n}-c${ci}`}>
                        {cl.lead ? <b>{cl.lead}</b> : null}{cl.lead ? ' ' : null}
                        {richText(cl.text, `${sec.n}-c${ci}`)}
                      </p>
                    ))}
                    {sec.keybox && (
                      <div className="keybox">
                        <div className="kh">
                          <span className="badge">{sec.keybox.badge}</span> {sec.keybox.heading}
                        </div>
                        {(sec.keybox.items || []).map((item, ii) => (
                          <div className="kitem" key={`${sec.n}-k${ii}`}>
                            <span className="kn">{ii + 1}</span>
                            <div>{richText(item.text, `${sec.n}-k${ii}`)}</div>
                          </div>
                        ))}
                      </div>
                    )}
                  </div>
                ))}
              </div>
              <div className={`readnote${scrolledToEnd ? ' ok' : ''}`}>
                {scrolledToEnd ? (
                  <>
                    <svg width="14" height="14" viewBox="0 0 14 14"><path d="M3 7.5l2.5 2.5L11 4.5" stroke="#48B64C" strokeWidth="1.6" fill="none" strokeLinecap="round" strokeLinejoin="round" /></svg>
                    You've read the full terms — you can continue
                  </>
                ) : (
                  <>
                    <svg width="14" height="14" viewBox="0 0 14 14"><circle cx="7" cy="7" r="6" stroke="#9A9A9A" strokeWidth="1.3" fill="none" /><path d="M7 4v3.5M7 9.5v.2" stroke="#9A9A9A" strokeWidth="1.4" strokeLinecap="round" /></svg>
                    Scroll to the end of the terms to continue
                  </>
                )}
              </div>
            </section>

            {/* STEP 3 — Initial the key clauses */}
            <section className={`step${step === 3 ? ' show' : ''}`} data-testid="fs-step-3" data-step="3">
              <p style={{ fontSize: '14px', margin: '-2px 0 4px', color: 'var(--slate)' }}>Type your initials beside each key clause to confirm you've read and agree to it.</p>
              {initRows.map((r) => {
                const value = initialsByClause[r.key] || '';
                const signed = clauseInitialled(r.key);
                return (
                  <div className={`initrow${signed ? ' signed' : ''}`} key={r.key}>
                    <div className="it"><b>{r.label}</b> — {r.desc}</div>
                    <input
                      type="text"
                      className="initbox"
                      data-testid={`fs-initials-${r.key}`}
                      value={value}
                      onChange={(e) => setClauseInitials(r.key, e.target.value)}
                      placeholder="Your initials"
                      aria-label={`Your initials for: ${r.label}`}
                      maxLength={INITIALS_MAX}
                      autoComplete="off"
                      autoCapitalize="characters"
                      inputMode="text"
                      spellCheck={false}
                    />
                  </div>
                );
              })}
              <p className="initnote">
                Type them yourself — we never fill these in for you. {isFacility ? 'Both are' : 'All three are'} required to continue.
              </p>
            </section>

            {/* STEP 4 — Review & sign */}
            <section className={`step${step === 4 ? ' show' : ''}`} data-testid="fs-step-4" data-step="4">
              <div className="rev">
                <div className="revrow"><span className="k">License fee</span><span className="v">{feeReviewLabel}</span></div>
                {isFacility && <div className="revrow"><span className="k">Start-up fee</span><span className="v">{fees?.start_up || '$1,500'} · one time, due at signing, non-refundable</span></div>}
                {!isFacility && <div className="revrow"><span className="k">Onsite training</span><span className="v">{onsiteOptin ? 'Added · $1,500 (+travel outside lower 48)' : 'No — remote + portal (standard)'}</span></div>}
                <div className="revrow"><span className="k">Authorizations</span>
                  <span className="v">{isFacility
                    ? 'Card on file (section 3.5) — authorized by signing'
                    : <>Card on file &amp; background/credit check — authorized by signing</>}</span>
                </div>
                {isFacility && (
                  <div className="revrow"><span className="k">Designated Fitter</span>
                    <span className="v" data-testid="fs-review-fitter">{fitterName || '—'}</span>
                  </div>
                )}
                <div className="revrow"><span className="k">Terms reviewed</span>
                  <span className="v ok">
                    <svg width="13" height="13" viewBox="0 0 13 13"><path d="M2.5 6.8l2.6 2.6L10.5 4" stroke="#48B64C" strokeWidth="2" fill="none" strokeLinecap="round" strokeLinejoin="round" /></svg>
                    Read in full
                  </span>
                </div>
                <div className="revrow"><span className="k">Key clauses initialed</span>
                  {initialedCount === initRows.length ? (
                    <span className="v ok">
                      <svg width="13" height="13" viewBox="0 0 13 13"><path d="M2.5 6.8l2.6 2.6L10.5 4" stroke="#48B64C" strokeWidth="2" fill="none" strokeLinecap="round" strokeLinejoin="round" /></svg>
                      All {initRows.length}
                    </span>
                  ) : (
                    <span className="v">{initialedCount} of {initRows.length}</span>
                  )}
                </div>
                <div className="revrow"><span className="k">{isFacility ? 'Facility address' : 'Shipping address'}</span>
                  <span className="v" data-testid="fs-review-shipping">
                    {[addressLine1, city, stateField, postalCode, country].filter(Boolean).join(', ') || '—'}
                  </span>
                </div>
                <div className="revrow"><span className="k">Billing address</span>
                  <span className="v" data-testid="fs-review-billing">
                    {billingSame
                      ? 'Same as shipping'
                      : ([billingLine1, billingCity, billingState, billingPostal, billingCountry]
                          .filter(Boolean).join(', ') || '—')}
                  </span>
                </div>
                <div className="revrow"><span className="k">Return terms</span><span className="v">30 days · you ship · $3,500 if unreturned</span></div>
              </div>
              <div className="grouptitle">Your signature</div>
              <div className="sigwrap">
                <div className="sigpad">
                  <canvas
                    ref={canvasRef}
                    onMouseDown={startDraw}
                    onMouseMove={moveDraw}
                    onMouseUp={endDraw}
                    onMouseLeave={endDraw}
                    onTouchStart={startDraw}
                    onTouchMove={moveDraw}
                    onTouchEnd={endDraw}
                  />
                  <div className={`sigplace${hasSignature ? ' hide' : ''}`}>Sign here</div>
                  <div className="sigx">✕ ______________________________</div>
                </div>
                <div className="sigbar"><span className="lab">Draw your signature above</span><button type="button" className="clearbtn" onClick={clearSig}>Clear</button></div>
              </div>
            </section>

            {/* STEP 5 — Done */}
            <section className={`step${step === 5 ? ' show' : ''}`} data-testid="fs-step-5" data-step="5">
              <div className="done">
                <div className="seal"><svg width="40" height="40" viewBox="0 0 40 40"><path d="M11 21l6 6 12-14" stroke="#fff" strokeWidth="3.4" fill="none" strokeLinecap="round" strokeLinejoin="round" /></svg></div>
                <h2>Agreement signed</h2>
                {/* The card-setup link is sent when an admin APPROVES the agreement, not at
                    signing (agreements_api.process_approve). The old copy promised it was
                    already on its way, so a fitter waited for mail that could not arrive until
                    a human had reviewed the record. This screen states the real sequence. */}
                {isFacility ? (
                <>
                <p>Your Facility Partner Agreement is signed. One more step: add the card on file for your start-up fee.</p>
                <div className="ref">Reference: {reference}</div>
                <div className="whatnext" data-testid="fs-facility-next">
                  <div className="wnh">What happens next</div>
                  <ol>
                    <li>
                      <b>Add your card on file — now.</b> The {fees?.start_up || '$1,500'} start-up fee is due at signing. Your card
                      is entered on our payment processor — Henry-Griffitts never sees or stores your card number.
                    </li>
                    <li>
                      <b>We charge the start-up fee and countersign.</b> The agreement takes effect the day Henry-Griffitts
                      signs it, and your license fee starts then.
                    </li>
                    <li>
                      <b>Your system, certification and launch.</b> Your fitting system ships, your Designated Fitter
                      certifies through CoFitter, and we set the date of your launch fit day together.
                    </li>
                  </ol>
                </div>
                {errorMsg && <div className="errnote">{errorMsg}</div>}
                <div className="doneactions">
                  {cardSetupPath ? (
                    <a className="btn btn-primary" href={cardSetupPath} data-testid="fs-add-card">
                      Add your card on file
                    </a>
                  ) : (
                    <span className="donelink">We'll send you a secure link to add your card on file.</span>
                  )}
                  <span className="donelink">Questions? Call 208-664-1700 or email hgsupport@henry-griffitts.com.</span>
                </div>
                </>
                ) : (
                <>
                <p>Your Fitter License &amp; Account Agreement is signed and with the Henry-Griffitts team for review.</p>
                <div className="ref">Reference: {reference}</div>

                <div className="whatnext">
                  <div className="wnh">What happens next</div>
                  <ol>
                    <li>
                      <b>We review your agreement.</b> We review new agreements within <b>two business days</b> —
                      so if you signed on a Friday, expect to hear from us by Tuesday.
                    </li>
                    <li>
                      <b>You add your card on file.</b> Once it's approved you'll get a separate secure email with a
                      private link. Your card is entered on our payment processor — Henry-Griffitts never sees or
                      stores your card number.
                    </li>
                    <li>
                      <b>Your starter kit and training.</b> Your kit and Certified Teacher training details follow
                      after that.
                    </li>
                  </ol>
                  <p className="wnfoot">
                    A copy of your signed agreement is on its way to your email now. Nothing else is needed from you
                    today — you can close this page.
                  </p>
                </div>

                {errorMsg && <div className="errnote">{errorMsg}</div>}

                <div className="doneactions">
                  <a className="btn btn-primary" href="https://henry-griffitts.com" data-testid="fs-done">
                    Done
                  </a>
                  {/* No hardcoded address: the confirmation email is sent from whatever
                      FROM_EMAIL is configured to, and telling them to reply to it always
                      reaches us even if that address changes. */}
                  <span className="donelink">Questions? Just reply to the confirmation email.</span>
                </div>
                </>
                )}
              </div>
            </section>

            {step < 5 && (
              <div className="navbar">
                <button type="button" className="btn btn-ghost" data-testid="fs-back" onClick={() => go(-1)} disabled={step === 0 || submitting}>Back</button>
                <span className="stepmeta">{stepMeta}</span>
                <button type="button" className="btn btn-primary" data-testid="fs-next" onClick={() => go(1)} disabled={submitting || !canContinue()}>{nextLabel}</button>
              </div>
            )}
          </div>
        </main>

        <p className="hint">
          <svg width="13" height="13" viewBox="0 0 13 13"><path d="M6.5 1l4 1.6v3.4c0 3-1.8 5.2-4 6-2.2-.8-4-3-4-6V2.6L6.5 1z" stroke="#9A9A9A" strokeWidth="1.2" fill="none" strokeLinejoin="round" /></svg>
          {isFacility ? '' : 'A copy of your signed agreement will be emailed to you. '}Henry-Griffitts · Coeur d'Alene, Idaho
        </p>
        </>
        )}
      </div>
    </div>
  );
}
